import type { ServiceDetail } from "./types";

export const MARKETING_DIGITALE: ServiceDetail = {
  slug: "marketing-digitale",
  category: "digital",
  name: "Marketing Digitale",
  hero: {
    eyebrow: "Marketing Digitale",
    title: "Crescita misurabile, non solo like.",
    subtitle:
      "Funnel multicanale per PMI italiane misurato su ROI reale e costo per lead, non su impression e vanity metrics. Dal posizionamento alla pipeline commerciale, ogni euro investito è rendicontato. Sede a Roma, certificati ISO 9001:2015.",
    bullets: [
      "Strategia integrata Meta, Google, LinkedIn ed email",
      "Tracciamento end-to-end dal click alla pipeline commerciale",
      "Dashboard di performance leggibile in 5 minuti",
      "Report mensile con costo per lead reale e azioni successive",
    ],
  },
  problem: [
    "La maggior parte dei founder di PMI investe in social, advertising e contenuti senza una regia: ogni canale fa storia a sé, le agenzie precedenti hanno lasciato account ads disordinati e dashboard incomprensibili. Alla fine nessuno sa quanto costa davvero un lead, e l'imprenditore smette di crederci.",
    "Allo stesso tempo, i budget vengono bruciati in campagne attivate \"per provare\", senza tracciamento delle conversioni reali, senza un piano editoriale e con creatività generiche che non parlano al target. I lead arrivano, ma sono freddi, non in target o duplicati.",
    "Il risultato è un brand poco riconoscibile online, una pipeline commerciale incostante e una sensazione diffusa che il marketing digitale \"non funzioni\" — quando in realtà manca il sistema che lo fa funzionare.",
  ],
  approach: [
    "Il nostro framework si chiama PMI Funnel ed è in 4 fasi: Awareness (farti trovare e ricordare dal target giusto), Acquisition (portare visite qualificate al sito o in LinkedIn DM), Conversion (trasformare visite in lead concreti), Retention (riattivare clienti dormienti e chiudere upsell).",
    "Partiamo dal posizionamento e dalla buyer persona reale, non da template generici. Scegliamo 2-3 canali sostenibili rispetto al budget — di solito Meta + Google Ads + LinkedIn per il B2B — e costruiamo creatività italiane che parlano al settore specifico (industria, servizi, edilizia, food).",
    "Tracciamento server-side con GA4, Google Tag Manager e — quando il CRM è integrato — attribuzione fino al deal chiuso: ogni lead è ricondotto al canale giusto e il ROI è stimabile in modo difendibile, non in base al modello \"ultimo click\" di default.",
    "Ogni mese rivediamo insieme cosa ha portato lead reali, cosa va spento, cosa va potenziato. Le decisioni si prendono al tavolo, sui dati, in 30 minuti — non con report di 80 pagine che nessuno legge.",
  ],
  deliverables: [
    {
      title: "Audit canali e tracciamento",
      detail:
        "Analisi degli account ads esistenti (Meta, Google, LinkedIn), del sito, di GA4 e del CRM. Output: gap report con fix prioritari.",
    },
    {
      title: "Content calendar editoriale",
      detail:
        "Piano editoriale a 3 mesi su LinkedIn e Instagram con format ricorrenti, hook e calls-to-action allineate alla pipeline commerciale.",
    },
    {
      title: "Setup advertising + tracking",
      detail:
        "Account ads riorganizzati, conversion tracking server-side, dashboard centralizzata Looker Studio con CPL e ROI per canale.",
    },
    {
      title: "Creatività e copywriting",
      detail:
        "Pacchetto mensile di creatività video, statiche, testi advertising e landing page italiane prodotte dal nostro team interno.",
    },
    {
      title: "A/B test plan",
      detail:
        "Roadmap mensile di test su titoli, creatività, audience e landing per migliorare il CPL nel tempo invece che restare stabili.",
    },
    {
      title: "Report mensile e board",
      detail:
        "Report di 1 pagina con i KPI chiave (CPL, MQL, SQL, ROI), commento qualitativo e azioni del mese successivo. Board di 30 minuti.",
    },
  ],
  process: [
    {
      step: "01",
      title: "Settimana 1-2 — Brief & audit",
      text: "AB MARK conduce brief commerciale e audit completo di canali, tracciamento e CRM. Tu fornisci accesso agli account ads, GA4, CRM e introduci il referente commerciale. Output condiviso: gap report scritto.",
    },
    {
      step: "02",
      title: "Settimana 3-4 — Setup",
      text: "AB MARK riorganizza gli account ads, implementa il tracciamento server-side, costruisce la dashboard centralizzata e produce le prime creatività. Tu approvi messaggi chiave e tone of voice.",
    },
    {
      step: "03",
      title: "Mese 2-3 — Lancio campagne",
      text: "AB MARK attiva e gestisce le prime campagne su 2-3 canali con ottimizzazioni settimanali. Tu condividi feedback dal team commerciale sulla qualità reale dei lead, indispensabile per affinare l'audience.",
    },
    {
      step: "04",
      title: "Ongoing — Gestione mensile",
      text: "AB MARK gestisce campagne, A/B test e scaling. Tu partecipi al board mensile di 30 minuti per leggere insieme i KPI e validare le decisioni del mese successivo. Contratti rinnovabili a 6 mesi.",
    },
  ],
  faq: [
    {
      q: "Qual è il budget media minimo consigliato per partire?",
      a: "In genere consigliamo di partire da almeno 1.500-2.000 € al mese di budget media (escluso il fee della nostra agenzia) per avere dati statisticamente significativi entro 60 giorni. Sotto questa soglia preferiamo lavorare prima sul posizionamento, sui contenuti organici LinkedIn e sul SEO.",
    },
    {
      q: "Su quali canali vi concentrate?",
      a: "I nostri canali principali sono Meta Ads (Facebook + Instagram) per B2C e local business, Google Ads (Search e Performance Max) per intent commerciale, LinkedIn Ads e LinkedIn organico per B2B, e l'email marketing per nurturing e retention. Scegliamo i 2-3 più adatti al tuo target — non spalmiamo budget ovunque.",
    },
    {
      q: "Quanto dura il contratto di gestione marketing?",
      a: "I contratti sono semestrali rinnovabili, mai vincolanti su periodi lunghi. Servono almeno 3 mesi per avere un quadro affidabile su CPL e ROI per canale. Se dopo 6 mesi non vedi una pipeline più sana, non hai vincoli di rinnovo.",
    },
    {
      q: "Conviene fare il marketing in-house o affidarsi a un'agenzia?",
      a: "Per PMI con team marketing inferiori a 2 persone, l'agenzia è quasi sempre più efficiente: accesso immediato a competenze multi-disciplinari (strategia, ads, copy, design, analytics) senza dover assumere e formare. Quando il team interno cresce, ci posizioniamo come consulenti strategici e formatori.",
    },
    {
      q: "Su quali KPI vi misurate?",
      a: "I KPI che teniamo sotto controllo ogni mese sono: costo per lead (CPL), tasso di conversione lead → MQL → SQL, costo per acquisizione cliente (CAC), ROI per canale, lifetime value (LTV) quando il CRM lo permette. Le impression, i like e i follower sono indicatori di supporto, mai obiettivi finali.",
    },
    {
      q: "Producete voi i contenuti creativi (video, grafiche, testi)?",
      a: "Sì, abbiamo copywriter e designer interni e una rete di videomaker freelance italiani. Possiamo coprire l'intero stack creativo dell'agenzia oppure integrarci con il tuo team marketing esistente, fornendo solo strategia, gestione ads e analytics.",
    },
  ],
  seo: {
    title: "Agenzia Marketing Digitale Roma | Lead per PMI | AB MARK",
    description:
      "Agenzia di marketing digitale a Roma per PMI italiane: lead generation B2B, advertising Meta/Google/LinkedIn ed email, contenuti tracciati fino al lead reale.",
  },
};
